import {FC, ReactNode} from 'react'
import {OpportunityImage, Opportunity} from '@features/insights/types'
import {useTranslation} from '@/translations/i18n'
import {formatLocaleDate} from '@/dates.ts'
import {ScrollArea, Scrollbar} from '@components/ui/scroll-area'

interface ReviewFeedbackImagesProps {
    images: OpportunityImage[]
    opportunityKeyName: Opportunity['keyName']
    isMobile?: boolean
}

const getEntityLabel = (image: OpportunityImage, fallbackLabel: string) =>
    image.entityTitle ?? `${fallbackLabel} ${image.entityExternalId}`

export const ReviewFeedbackImages: FC<ReviewFeedbackImagesProps> = ({images, opportunityKeyName, isMobile}) => {
    const {t} = useTranslation()
    const listingFallback = t('commons:listing')

    const Wrapper = ({children}: {children: ReactNode}) =>
        isMobile ? (
            <div className="w-full pb-6 border-b border-neutral-300">{children}</div>
        ) : (
            <ScrollArea
                className="h-full w-full min-h-0 pr-6 border-r border-neutral-300"
                scrollbar={<Scrollbar className="m-1" />}
            >
                {children}
            </ScrollArea>
        )

    return (
        <Wrapper>
            <div className="flex flex-col gap-4 pt-0 px-6 pb-6 md:pt-4 md:pr-0 md:pb-6 md:pl-6 [&_h4]:m-0 [&_h4]:font-medium [&_h4]:text-md">
                <h4>{t('insights:issues:review_image_title')}</h4>

                <div className="grid grid-cols-1 gap-6 w-full lg:grid-cols-2 xl:grid-cols-3">
                    {images.map((image, index) => {
                        if (!image?.thumbnails) return null
                        const entityLabel = getEntityLabel(image, listingFallback)
                        const dateLabel = image.uploadedAt ? formatLocaleDate(image.uploadedAt, 'PP') : ''

                        return (
                            <div
                                className="flex flex-col w-full min-w-0 [&_p]:mt-4 [&_p]:mb-0.5 [&_p]:text-sm [&_p]:font-medium [&_p]:w-full [&_p]:text-ellipsis [&_p]:overflow-hidden [&_p]:whitespace-nowrap"
                                key={`${image.id}-${index}-${opportunityKeyName}`}
                            >
                                <div
                                    className="block w-full rounded-lg overflow-hidden [&_img]:w-full [&_img]:h-full [&_img]:aspect-[3/2] [&_img]:object-cover [&_img]:border [&_img]:border-black/20"
                                    key={image.id}
                                >
                                    <img src={image.thumbnails.xs} alt={image.name} />
                                </div>
                                <p title={image.name}>{image.name}</p>
                                <div className="text-sm text-neutral-600 w-full text-ellipsis overflow-hidden whitespace-nowrap [&_.separator]:font-bold [&_.separator]:mx-1">
                                    {dateLabel}
                                    {entityLabel && (
                                        <>
                                            <span className="separator">·</span>
                                            <span title={entityLabel}>{entityLabel}</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </Wrapper>
    )
}
